import { Accordion, ActionIcon } from '@mantine/core';
import { ListFilter, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { FC, useEffect, useState } from 'react';

import PatientDuplicationPopup from '@/components/AppointmentScheduler/PatientDuplicationPopup.tsx';
import { RescheduleNotificationModal } from '@/components/AppointmentScheduler/reschedule-notification-modal.tsx';
import CalendarDatePicker from '@/components/Filtering/CalendarDatePicker.tsx';
import CalendarFilter from '@/components/Filtering/CalendarFilter.tsx';

import { useBookAppointmentCollection } from '@/hooks/use-book-appointment-collection.ts';
import { useUpdateCollectionWithGuard } from '@/hooks/use-update-collection-with-guard.ts';
import useConfig from '@/hooks/useConfig.ts';

import { AvailabilityStatus } from '@/models/appointment/Appointment.ts';

import { showErrorNotification } from '@/services/notificationService.ts';

import { useBookingStore } from '@/stores/bookingStore.ts';
import { useCalendarStore } from '@/stores/calendarStore.ts';
import { useFilterStore } from '@/stores/filterStore.ts';

import { confirmExitSeriesPlacement, removeSeriesPlacement } from '@/utils/calendarUtils.ts';
import { cn } from '@/utils/styleUtils.ts';

import Icon from '../Icon.tsx';
import CalendarLegend from './Legend.tsx';
import SeriesPlacementPanel from './SeriesPlacementPanel.tsx';

const CalendarSidebar: FC = () => {
    const expanded_accordions = useCalendarStore((state) => state.expanded_accordions);
    const is_sidebar_open = useCalendarStore((state) => state.is_sidebar_open);
    const { location_key } = useConfig();
    const { bookCollection } = useBookAppointmentCollection();
    const editingCollectionId = useBookingStore((state) => state.editingCollectionId);
    const expanded_accordions_per_location = expanded_accordions[location_key] ?? ['calendar', 'display'];

    const isSeriesPlacementMode = useBookingStore((state) => state.isSeriesPlacementMode);
    const seriesPlacementConfig = useBookingStore((state) => state.seriesPlacementConfig);
    const seriesPlacedAppointments = useBookingStore((state) => state.seriesPlacedAppointments);
    const isBookingLoading = useBookingStore((state) => state.is_booking_loading);
    const [showDuplicationPopup, setShowDuplicationPopup] = useState(false);
    const [showRescheduleModal, setShowRescheduleModal] = useState(false);

    // Clean up placement state when navigating AWAY from calendar.
    // Pathname guard: React 18 StrictMode remounts components in dev (mount → unmount → mount),
    // which triggers this cleanup while still on /calendar-view and wipes placement state.
    // Checking pathname distinguishes real navigation (pathname changed) from StrictMode unmount (pathname unchanged).
    // See: https://react.dev/reference/react/StrictMode#fixing-bugs-found-by-re-running-effects-in-development
    useEffect(() => {
        return (): void => {
            if (
                useBookingStore.getState().isSeriesPlacementMode &&
                !globalThis.location.pathname.includes('calendar-view')
            ) {
                useBookingStore.setState({
                    isSeriesPlacementMode: false,
                    seriesPlacementConfig: undefined,
                    seriesPlacedAppointments: [],
                });
            }
        };
    }, []);

    const handleAccordionChange = (value: string[]): void => {
        useCalendarStore.setState({
            expanded_accordions: {
                ...expanded_accordions,
                [location_key]: value,
            },
        });
    };

    const toggleSidebar = (): void => {
        useCalendarStore.setState({ is_sidebar_open: !is_sidebar_open });
    };

    const handleExitPlacement = (): void => {
        const userPlacedCount = seriesPlacedAppointments.filter((p) => !p.isPast).length;
        confirmExitSeriesPlacement(location_key, userPlacedCount);
    };

    const onPlacementComplete = (): void => {
        const previousSelection = useBookingStore.getState().seriesPlacementConfig?.previousCalendarSelection;
        if (previousSelection) {
            useFilterStore.setState({
                selected_calendars: {
                    ...useFilterStore.getState().selected_calendars,
                    [location_key]: previousSelection,
                },
            });
        }
        useBookingStore.setState({
            isSeriesPlacementMode: false,
            seriesPlacementConfig: undefined,
            seriesPlacedAppointments: [],
        });
    };

    const updateCollectionWithGuard = useUpdateCollectionWithGuard(onPlacementComplete);

    const handleConfirmPlacement = async (): Promise<void> => {
        const futurePlacements = seriesPlacedAppointments.filter((p) => !p.isPast);
        const occurrences = futurePlacements.map((placed) => ({
            id: placed.id,
            start_datetime: placed.startDatetime,
            end_datetime: placed.endDatetime,
            availability: AvailabilityStatus.AVAILABLE,
        }));

        useBookingStore.setState({ collection_occurrences: occurrences });

        if (editingCollectionId) {
            setShowRescheduleModal(true);
            return;
        }

        const result = await bookCollection({ onClose: onPlacementComplete, isOverlap: true });
        if (result?.needsDuplication) {
            setShowDuplicationPopup(true);
        }
    };

    const handleConfirmDuplication = async (): Promise<void> => {
        useBookingStore.setState({ patient_duplication_allowed: true });
        setShowDuplicationPopup(false);
        const result = await bookCollection({ onClose: onPlacementComplete, isOverlap: true });
        if (result?.needsDuplication) {
            useBookingStore.setState({ patient_duplication_allowed: false });
            showErrorNotification('Fehler bei der Patientenduplizierung. Bitte versuchen Sie es erneut.');
        }
    };

    const getSidebarWidth = (): string => {
        if (isSeriesPlacementMode) return 'w-[265px]';
        if (is_sidebar_open) return 'w-[250px]';
        return 'w-0 overflow-hidden';
    };

    return (
        <div className="relative">
            {!isSeriesPlacementMode && (
                <ActionIcon
                    onClick={toggleSidebar}
                    className={cn(
                        'absolute top-1 z-20 rounded-full border-gray-300 bg-white shadow-md hover:bg-gray-50',
                        is_sidebar_open ? '-right-[4.3rem]' : '-right-[2.7rem]'
                    )}
                    size="xl"
                >
                    {is_sidebar_open ? (
                        <Icon color="text-black" icon={PanelLeftClose} />
                    ) : (
                        <Icon color="text-black" icon={PanelLeftOpen} />
                    )}
                </ActionIcon>
            )}
            <div className={cn('flex flex-col justify-between pb-8 transition-all duration-300', getSidebarWidth())}>
                <div className="flex flex-col gap-4">
                    <CalendarDatePicker />
                    {isSeriesPlacementMode && seriesPlacementConfig ? (
                        <SeriesPlacementPanel
                            patientName={seriesPlacementConfig.patientName}
                            appointmentTypeName={seriesPlacementConfig.appointmentTypeName}
                            calendarName={seriesPlacementConfig.calendarName}
                            durationMinutes={seriesPlacementConfig.durationMinutes}
                            totalAppointments={seriesPlacementConfig.totalAppointments}
                            placedAppointments={seriesPlacedAppointments}
                            onClose={handleExitPlacement}
                            onBack={handleExitPlacement}
                            onConfirm={handleConfirmPlacement}
                            onRemove={removeSeriesPlacement}
                            confirmLabel={editingCollectionId ? 'Aktualisieren' : 'Erstellen'}
                            isLoading={isBookingLoading}
                        />
                    ) : (
                        <Accordion
                            classNames={{
                                control: 'px-0',
                                content: 'px-0',
                            }}
                            className="w-full overflow-y-auto"
                            value={expanded_accordions_per_location}
                            onChange={handleAccordionChange}
                            multiple
                        >
                            <Accordion.Item value="calendar">
                                <Accordion.Control icon={<ListFilter />}>Kalender filtern</Accordion.Control>
                                <Accordion.Panel>
                                    <CalendarFilter />
                                </Accordion.Panel>
                            </Accordion.Item>
                        </Accordion>
                    )}
                </div>
                {!isSeriesPlacementMode && (
                    <div className="flex w-full">
                        <CalendarLegend />
                    </div>
                )}
            </div>
            <PatientDuplicationPopup
                visible={showDuplicationPopup}
                setVisible={setShowDuplicationPopup}
                setConfirm={handleConfirmDuplication}
            />
            <RescheduleNotificationModal
                visible={showRescheduleModal}
                setVisible={setShowRescheduleModal}
                onConfirm={() => updateCollectionWithGuard(true)}
                onCancel={() => updateCollectionWithGuard(false)}
            />
        </div>
    );
};

export default CalendarSidebar;
